import { useMemo } from 'react'
import { useProductQuery, ProductQuery } from 'src/apollo/onlineOrdering'
import { Channel } from 'src/public/js/siteUtilities'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useDisplayPageContext } from '../../DisplayPageContext'

export type Product = NonNullable<ProductQuery['product']>
export type ProductVariant = Product['variants'][number]

/**
 * Hook to fetch product with variants data by slug using the GraphQL API.
 *
 * @param productSlug - The slug of the product to fetch
 * @returns The product data from the API, loading state, and any errors
 */
export const useProductVariants = (productSlug?: string) => {
  const ooClient = useOOClient()
  const { selectedLocation } = useRestaurant()
  const { channelGuid, channel } = useDisplayPageContext()

  const restaurantGuid = selectedLocation?.externalId || ''
  const viewOnlyMenuChannelGuid = selectedLocation?.viewOnlyMenuChannelGuid

  const primaryChannelGuid =
    channel === Channel.OO ? viewOnlyMenuChannelGuid : channelGuid

  const { data, loading, error } = useProductQuery({
    variables: {
      input: {
        slug: productSlug || '',
        restaurantGuid,
        channelGuid: primaryChannelGuid
      }
    },
    client: ooClient,
    skip: !productSlug || !restaurantGuid
  })

  const product: Product | undefined = useMemo(() => {
    if (!data?.product) {
      return undefined
    }
    return data.product
  }, [data])

  return {
    product,
    loading,
    error: error ?? undefined
  }
}
